import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTonConnectModal } from '@tonconnect/ui-react';

import { usePaymentMutation } from '@/api/queries/payment';
import { useGetMyInfoMutation } from '@/api/queries/user';
import { ToastNotification } from '@/common/components/ToastNotification/ToastNotification';
import { additionalDiscountForUsdt } from '@/common/config/common';
import { useStorePrice } from '@/common/hooks/apple-price';
import { useBuyCoinRequest } from '@/common/hooks/buyCoinRequest';
import { usePaymentStatus } from '@/common/hooks/payment-status';
import { usePurchaseAttempt } from '@/common/hooks/purchase-attempt';
import { multiplication } from '@/common/lib/decimal';
import { Playdeck, SendAnalitics } from '@/common/lib/playdeck';
import { type BuyCoinItemProps } from '@/common/types/components';
import { type TgemPurchaseSource } from '@/common/types/modal';
import {
  H1,
  H2,
  StringMain,
  StringSmall,
} from '@/components/Typography/Typography';
import { GreyBlurButton } from '@/components/ui/Button/Button';
import { Close } from '@/components/ui/Close/Close';
import TONandStarsBuyButtons from '@/components/ui/TONandStarsBuyButtons/TONandStarsBuyButtons';
import PendingStatus from '@/components/ui/ViewStatusComponents/PendingStatus';
import SuccessfullStatus from '@/components/ui/ViewStatusComponents/SuccessfullStatus';
import {
  changePaymentStatus,
  clearPendingPurchase,
  setPendingPurchase,
} from '@/redux/reducers/paymentsReducer';
import { useAppDispatch } from '@/redux/store';

import { ModalContext } from '../Modal/config';

import coinPng from '/img/tgem.png';
import coinWebp from '/img/tgem.webp';
import tgemBigPng from '/img/tgem-big.png';
import tgemBigWebp from '/img/tgem-big.webp';

interface prop {
  activeOffer: BuyCoinItemProps;
  source: TgemPurchaseSource;
}

const BuySingleTgems: React.FC<prop> = ({ activeOffer, source }) => {
  const { t } = useTranslation();
  const { price, coin, starPrice } = activeOffer;
  const { closeModal, secondModal, thirdModal } = useContext(ModalContext);
  const paymentStatus = usePaymentStatus();
  const dispatch = useAppDispatch();
  const [waiting, setWaiting] = useState<boolean>(false);
  const usdPrice = useMemo(
    () => multiplication(price, additionalDiscountForUsdt.coefficient),
    [price],
  );

  usePurchaseAttempt({
    item: 'tgem',
    item_id: coin.toString(),
    price,
    currency: 'usdt',
  });

  const buildExtras = (
    currency: 'ton' | 'xtr' | 'usdt',
  ): Record<string, unknown> => ({
    coin,
    price,
    currency,
    source,
  });

  const onBuyCoins = useBuyCoinRequest();
  const [buyCoins] = usePaymentMutation();
  const [getMyInfo] = useGetMyInfoMutation();
  const { state } = useTonConnectModal();

  const sendAnaliticsPending = (currency: 'ton' | 'xtr'): void => {
    SendAnalitics.purchasePending({
      item: 'tgem',
      item_id: coin.toString(),
      price,
      currency,
    });
  };

  const sendAnaliticsSuccessful = (currency: 'usdt'): void => {
    SendAnalitics.purchaseSuccessfull({
      item: 'tgem',
      item_id: coin.toString(),
      price,
      currency,
    });
    Playdeck.sendAnalitics({
      type: 'custom_event',
      name: 'tgem_purchase_completed',
      user_properties: {},
      event_properties: buildExtras(currency),
    });
  };

  const onClick = (currency: 'XTR' | 'TON'): void => {
    setWaiting(true);
    onBuyCoins(currency, activeOffer)
      .then(() => {
        const lowerCurrency = currency.toLowerCase() as 'ton' | 'xtr';
        dispatch(changePaymentStatus('pending'));
        dispatch(
          setPendingPurchase({
            item: 'tgem',
            item_id: coin.toString(),
            price,
            currency: lowerCurrency,
            extras: buildExtras(lowerCurrency),
          }),
        );
        sendAnaliticsPending(lowerCurrency);
        setWaiting(false);
      })
      .catch(() => {
        dispatch(changePaymentStatus(undefined));
        dispatch(clearPendingPurchase());
        setWaiting(false);
      });
  };

  const buyUsdt = async (): Promise<void> => {
    try {
      await buyCoins({ type: 'tgem', amount: coin, currency: 'USDT' }).unwrap();

      if (thirdModal.isOpen) {
        thirdModal.closeModal();
      } else {
        secondModal.closeModal();
      }
      dispatch(changePaymentStatus('success'));
      sendAnaliticsSuccessful('usdt');
      await getMyInfo();
      ToastNotification(t('Success. T-Gem credited and ready to use'), {
        img: coinPng,
        webp: coinWebp,
      });
    } catch (error) {
      console.error(error);
      ToastNotification(t('Something went wrong, please try again'), {
        img: coinPng,
        webp: coinWebp,
      });
      dispatch(changePaymentStatus(undefined));
    }
  };

  useEffect(() => {
    if (state.closeReason === 'action-cancelled') setWaiting(false);
  }, [state.closeReason]);

  const handleClose = (): void => {
    closeModal();
    secondModal.closeModal();
    dispatch(changePaymentStatus(undefined));
  };

  const storePrice = useStorePrice(price);

  return (
    <div className="flex flex-col items-center pt-[200px] gap-3 px-6 h-full pb-[4.5rem]">
      {paymentStatus === 'pending' ? (
        <PendingStatus />
      ) : paymentStatus === 'success' ? (
        <SuccessfullStatus>
          <div className="bg-white/10 rounded-[8px] relative p-[13px] opacity-[0.6]">
            <picture>
              <source srcSet={tgemBigWebp} type="image/webp" />
              <img src={tgemBigPng} alt="resource" width={108} />
            </picture>
            <StringSmall
              weight="bold"
              className="bg-black/30 absolute bottom-[4px] left-1/2 -translate-x-1/2 !text-[24px] px-[8px] py-[4px] rounded-[8px]"
            >
              {coin}
            </StringSmall>
          </div>
        </SuccessfullStatus>
      ) : (
        <>
          <div className="flex flex-col gap-6 items-center">
            <H1>{t('Purchase')}</H1>
            <div className="text-center">
              <H2 className="mb-[4px]">T-Gems</H2>
              <StringSmall className="text-[14px] text-white/60">
                {t('The fuel for your rise. Spend wisely, grow boldly!')}
              </StringSmall>
            </div>
            <div className="bg-white/10 rounded-[8px] relative p-[13px]">
              <picture>
                <source srcSet={tgemBigWebp} type="image/webp" />
                <img src={tgemBigPng} alt="resource" width={108} />
              </picture>
              <StringSmall
                weight="bold"
                className="bg-black/30 absolute bottom-[4px] left-1/2 -translate-x-1/2 !text-[24px] px-[8px] py-[4px] rounded-[8px]"
              >
                {coin}
              </StringSmall>
            </div>
          </div>
          <StringMain weight="extrabold" className="!text-[1.5rem] mt-auto">
            {storePrice ?? `$ ${price}`}
          </StringMain>
          <TONandStarsBuyButtons
            onClick={onClick}
            isLoading={waiting}
            days={coin === 10 ? 1 : 2}
            price={price}
            usdPrice={usdPrice}
            discount={additionalDiscountForUsdt.percentage}
            starPrice={starPrice}
            classNames="!px-0 mt-1"
            mustBeUsdtButton={true}
            onUsdtBuy={buyUsdt}
            variant="switch"
          />
        </>
      )}
      {!paymentStatus && (
        <GreyBlurButton onClick={handleClose} text={t('Close')} />
      )}
      {paymentStatus === 'pending' && (
        <Close
          onClick={handleClose}
          imageVariant="modal"
          size="lg"
          className="absolute bottom-[calc(var(--tg-safe-area-inset-bottom,0px)+30px)]"
        />
      )}
    </div>
  );
};

export default BuySingleTgems;
